import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;
const LOGO_SRC = `${process.env.PUBLIC_URL}/Assets/Images/color icon_circle.png`;
const PLAY_QR = `${process.env.PUBLIC_URL}/Assets/Images/qr-code.png`;

function subscribeMessage(payload, fallback) {
  if (!payload || typeof payload !== 'object') return fallback;
  return payload.message || payload.error || fallback;
}

function isAlreadySubscribed(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.error === 'Already subscribed') return true;
  return /already subscribed/i.test(String(payload.message || payload.error || ''));
}

function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const noticeTimer = useRef(null);

  const clearNoticeTimer = () => {
    if (noticeTimer.current) {
      window.clearTimeout(noticeTimer.current);
      noticeTimer.current = null;
    }
  };

  const hideNoticeAfter = (clearNotice) => {
    clearNoticeTimer();
    noticeTimer.current = window.setTimeout(() => {
      clearNotice();
      noticeTimer.current = null;
    }, 3000);
  };

  useEffect(() => () => clearNoticeTimer(), []);

  const handleSubscribe = async (event) => {
    event.preventDefault();
    setError('');
    setSubscribed(false);
    clearNoticeTimer();
    setSending(true);

    try {
      if (!API_URL) {
        throw new Error('Service is not configured.');
      }

      const { default: axios } = await import('axios');
      const { data } = await axios.post(API_URL, {
        action: 'subscribe',
        email: email.trim(),
      });

      if (data?.success === false || isAlreadySubscribed(data)) {
        setError(subscribeMessage(data, 'This email is already subscribed.'));
        hideNoticeAfter(() => setError(''));
        return;
      }

      setEmail('');
      setSubscribed(true);
      hideNoticeAfter(() => setSubscribed(false));
    } catch (err) {
      setSubscribed(false);
      setError(subscribeMessage(err.response?.data, err.message || 'Could not subscribe. Please try again.'));
      hideNoticeAfter(() => setError(''));
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-brand">
          <Link to="/" className="footer-lockup" aria-label="InstaTenders home">
            <img src={LOGO_SRC} alt="" className="footer-mark" width="192" height="192" decoding="async" />
            <span>InstaTenders</span>
          </Link>
          <p className="playstore-label">Playstore</p>
          <div className="qr-wrap">
            <img
              src={PLAY_QR}
              alt="Download InstaTenders on Google Play"
              width="256"
              height="256"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>

        <div className="footer-block">
          <h3>Address</h3>
          <p>
            <strong>InstaTenders Solutions Pvt Ltd</strong>
            4/150, Labour Colony 1st St, 3rd Phase,
            Thiru Vi Ka Industrial Estate, SIDCO Industrial Estate,
            Guindy, Chennai,<br />
            Tamil Nadu 600032.
          </p>
        </div>

        <div className="footer-block">
          <h3>Contact</h3>
          <ul className="footer-contact">
            <li>
              <a href="mailto:info@instatenders.in">info@instatenders.in</a>
            </li>
            <li>
              <a href="tel:+919150081257">+91 91500 81257</a>
              {' - '}
              <a href="tel:+919150081260">60</a>
            </li>
           
          </ul>
          <a
            className="play-badge"
            href="https://play.google.com/store/apps/details?id=com.instatenders"
            target="_blank"
            rel="noreferrer"
          >
            <span className="play-icon" aria-hidden="true" />
            <span>
              <small>GET IT ON</small>
              Google Play
            </span>
          </a>
        </div>

        <div className="footer-block">
          <h3>Sites</h3>
          <ul className="footer-contact">
            <li>
              <a href="https://scm.instatenders.in" target="_blank" rel="noreferrer">
                scm.instatenders.in
              </a>
            </li>
            <li>
              <a href="https://leads.instatenders.in" target="_blank" rel="noreferrer">
                leads.instatenders.in
              </a>
            </li>
          </ul>
        </div>

        <div className="footer-block footer-subscribe">
          <h3>Stay Connected</h3>
          <form onSubmit={handleSubscribe}>
            <label htmlFor="footer-email">Email *</label>
            <input
              id="footer-email"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="email"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setError('');
              }}
            />
            {subscribed && <p className="footer-subscribe-success">Thank you for subscribing.</p>}
            {error && <p className="footer-subscribe-error">{error}</p>}
            <button type="submit" disabled={sending}>
              {sending ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
        </div>
      </div>

      <div className="container footer-copy">
        <div className="footer-social">
          <a
            href="https://www.instagram.com/instatenders/"
            target="_blank"
            rel="noreferrer"
            aria-label="InstaTenders on Instagram"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm0 2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7zm10.25 1.25a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 8.2A3.8 3.8 0 1 1 8.2 12 3.8 3.8 0 0 1 12 8.2zm0 2a1.8 1.8 0 1 0 1.8 1.8A1.8 1.8 0 0 0 12 10.2z" />
            </svg>
            Instagram
          </a>
          <a
            href="https://www.linkedin.com/company/instatenders-solutions-private-limited/"
            target="_blank"
            rel="noreferrer"
            aria-label="InstaTenders on LinkedIn"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6.5 9.5H4V20h2.5V9.5zM5.25 4A1.75 1.75 0 1 0 7 5.75 1.75 1.75 0 0 0 5.25 4zM20 20h-2.5v-5.6c0-1.86-.66-2.9-2.05-2.9-1.12 0-1.78.76-2.07 1.5-.11.26-.09.62-.09.98V20H11s.04-9.2 0-10.16h2.5v1.44c.33-.51 1.47-1.74 3.58-1.74C19.4 9.54 20 11.4 20 14.18V20z" />
            </svg>
            LinkedIn
          </a>
        </div>
        <p className="footer-copy-legal">
          {new Date().getFullYear()} © InstaTenders Solutions Pvt Ltd, All rights reserved.
          <Link to="/privacy-policy">Privacy policy</Link>
        </p>
      </div>
    </footer>
  );
}

export default Footer;
